import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Trash2, ShoppingBag, ArrowRight } from 'lucide-react';
import Layout from '../components/layout/Layout';
import SEO from '../components/seo/SEO';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import ProductCarousel from '../components/products/ProductCarousel';
import ProductGridSkeleton from '../components/products/ProductGridSkeleton';
import { getBestSellers } from '../services/products';
import { formatCurrency } from '../lib/currency';
import { BRAND_NAME } from '../lib/brand';

const WishlistPage = () => {
  const { t } = useTranslation();
  const { wishlistItems, toggleWishlist, addToCart } = useCart();
  const [suggestedProducts, setSuggestedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSuggested = async () => {
      try {
        const products = await getBestSellers();
        const filtered = products
          .filter((p) => !wishlistItems.find((item) => item.id === p.id))
          .slice(0, 8);
        setSuggestedProducts(filtered);
      } catch (error) {
        console.error('Error fetching suggested products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSuggested();
  }, [wishlistItems]);

  const handleAddToCart = (product) => {
    if (product.sizes && product.sizes.length > 0 && product.colorNames && product.colorNames.length > 0) {
      addToCart(product, product.sizes[0], product.colorNames[0], 1);
    }
  };

  if (wishlistItems.length === 0) {
    return (
      <Layout>
        <SEO title={`${BRAND_NAME} | İstek Listesi`} description="Favori ürünlerinizi kaydedin" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <ShoppingBag size={64} className="mx-auto text-gray-300 mb-6" />
            <h1 className="text-2xl font-semibold mb-4">İstek listeniz boş</h1>
            <p className="text-gray-600 mb-8">
              Beğendiğiniz ürünleri istek listenize kaydedin.
            </p>
            <Link to="/">
              <Button size="lg" className="bg-black hover:bg-gray-800">
                Alışverişe Başla
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="mt-16">
              <h2 className="text-2xl font-bold mb-6 text-center">Beğenebileceğiniz Ürünler</h2>
              <ProductGridSkeleton columns={4} count={4} />
            </div>
          ) : suggestedProducts.length > 0 && (
            <div className="mt-16">
              <ProductCarousel
                title="Beğenebileceğiniz Ürünler"
                products={suggestedProducts}
              />
            </div>
          )}
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEO title={`${BRAND_NAME} | İstek Listesi`} description="Favori ürünleriniz" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-semibold mb-8">
          İstek Listesi ({wishlistItems.length} {wishlistItems.length === 1 ? 'ürün' : 'ürün'})
        </h1>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
          {wishlistItems.map((item) => (
            <div key={item.id} className="group">
              {/* Image */}
              <Link to={`/product/${item.id}`}>
                <div className="relative aspect-[3/4] overflow-hidden rounded-lg bg-gray-100">
                  <img
                    src={item.images[0]}
                    alt={item.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      toggleWishlist(item);
                    }}
                    className="absolute top-3 right-3 p-2 rounded-full bg-white shadow-sm hover:bg-gray-100 transition-colors"
                    aria-label="Remove from wishlist"
                  >
                    <Trash2 size={16} className="text-gray-600" />
                  </button>
                </div>
              </Link>

              {/* Info */}
              <div className="mt-3">
                <Link to={`/product/${item.id}`}>
                  <h3 className="font-medium text-sm group-hover:text-blue-600 transition-colors line-clamp-2">
                    {item.name}
                  </h3>
                </Link>
                <div className="flex items-center gap-2 mt-1">
                  <span className="font-semibold">{formatCurrency(item.price)}</span>
                  {item.originalPrice && (
                    <span className="text-sm text-gray-500 line-through">
                      {formatCurrency(item.originalPrice)}
                    </span>
                  )}
                </div>
                <Button
                  onClick={() => handleAddToCart(item)}
                  variant="outline"
                  size="sm"
                  className="w-full mt-3"
                >
                  Sepete Ekle
                  <ArrowRight size={14} className="ml-2" />
                </Button>
              </div>
            </div>
          ))}
        </div>

        {loading ? (
          <div className="mt-16">
            <h2 className="text-2xl font-bold mb-6">Beğenebileceğiniz Ürünler</h2>
            <ProductGridSkeleton columns={4} count={4} />
          </div>
        ) : suggestedProducts.length > 0 && (
          <div className="mt-16">
            <ProductCarousel
              title="Beğenebileceğiniz Ürünler"
              products={suggestedProducts}
            />
          </div>
        )}
      </div>
    </Layout>
  );
};

export default WishlistPage;
